/**
 * File Upload Constants
 * 
 * This file contains all constants related to file upload functionality,
 * including supported file types, size limits, and validation rules.
 */

/**
 * Supported file extensions for NACHA file upload
 */
export const SUPPORTED_FILE_EXTENSIONS = ['.txt', '.ach', '.nacha', '.dat'] as const;

/**
 * MIME type for file input accept attribute
 */
export const SUPPORTED_MIME_TYPES = '.txt,.ach,.nacha,.dat,text/plain';

/**
 * Maximum file size in bytes (10 MB)
 */
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Maximum file size in MB (for display purposes)
 */
export const MAX_FILE_SIZE_MB = 10;

/**
 * File reading timeout in milliseconds (30 seconds)
 */
export const FILE_READ_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Get a human-readable string of supported file extensions
 * @returns Comma-separated list of extensions (e.g., ".txt, .ach, .nacha, .dat")
 */
export const getSupportedExtensionsString = (): string => {
  return SUPPORTED_FILE_EXTENSIONS.join(', ');
};

/**
 * Check if a file extension is supported
 * @param fileName - The file name to check
 * @returns True if the file extension is supported or file has no extension
 */
export const isSupportedFileExtension = (fileName: string): boolean => {
  const lowerCaseFileName = fileName.toLowerCase();
  
  // Check if file has no extension (no dot in filename, or dot only at start for hidden files)
  const lastDotIndex = lowerCaseFileName.lastIndexOf('.');
  const hasNoExtension = lastDotIndex === -1 || lastDotIndex === 0;
  
  // Allow files with no extension or with supported extensions
  return hasNoExtension || SUPPORTED_FILE_EXTENSIONS.some(ext => lowerCaseFileName.endsWith(ext));
};

/**
 * Check if a file size is within the allowed limit
 * @param fileSizeBytes - The file size in bytes
 * @returns True if the file size is within the limit
 */
export const isFileSizeValid = (fileSizeBytes: number): boolean => {
  return fileSizeBytes <= MAX_FILE_SIZE_BYTES;
};

/**
 * Convert bytes to KB with one decimal place
 * @param bytes - The number of bytes
 * @returns Formatted string (e.g., "123.4")
 */
export const formatBytesToKB = (bytes: number): string => {
  return (bytes / 1024).toFixed(1);
};

/**
 * Convert bytes to MB with two decimal places
 * @param bytes - The number of bytes
 * @returns Formatted string (e.g., "1.23")
 */
export const formatBytesToMB = (bytes: number): string => {
  return (bytes / (1024 * 1024)).toFixed(2);
};

/**
 * Error messages for file upload validation
 */
export const FILE_UPLOAD_ERRORS = {
  INVALID_TYPE: (fileName: string) => 
    `Invalid file type: "${fileName}". Please upload a NACHA file (${getSupportedExtensionsString()} or no extension)`,
  FILE_TOO_LARGE: (sizeKB: string) => 
    `File too large: ${sizeKB} KB. Maximum size is ${MAX_FILE_SIZE_MB} MB.`,
  READ_TIMEOUT: () => 
    `File reading timeout (${FILE_READ_TIMEOUT_MS / 1000}s)`,
  READ_FAILED: (error: string) => 
    `Failed to read file: ${error}`,
} as const;

/**
 * Success messages for file upload
 */
export const FILE_UPLOAD_SUCCESS = {
  UPLOADED: (fileName: string, sizeKB: string) => 
    `${fileName} (${sizeKB} KB) uploaded successfully`,
  PARSED: (lineCount: number, parseTime: number) => 
    `Parsed successfully! ${lineCount} records in ${parseTime.toFixed(0)}ms`,
} as const;

